// ─── getExpirationDisplay ─────────────────────────────────────────────────────

export interface ExpirationDisplay {
  text:   string
  color:  string
  urgent: boolean
}

export function getExpirationDisplay(expirationDate: string | null): ExpirationDisplay | null {
  if (!expirationDate) return null

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const expDate = new Date(expirationDate + 'T00:00:00')
  expDate.setHours(0, 0, 0, 0)

  const daysUntil = Math.floor((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

  if (daysUntil < 0) {
    const n = Math.abs(daysUntil)
    return { text: `Expired ${n} day${n === 1 ? '' : 's'} ago`, color: '#C25B4E', urgent: true }
  }
  if (daysUntil === 0) return { text: 'Expires today',    color: '#C25B4E', urgent: true }
  if (daysUntil === 1) return { text: 'Expires tomorrow', color: '#C25B4E', urgent: true }
  if (daysUntil <= 3) {
    return { text: `Expires in ${daysUntil} days`, color: '#D4863A', urgent: true }
  }
  if (daysUntil <= 7) {
    const dayName = expDate.toLocaleDateString('en-US', { weekday: 'long' })
    return { text: `Expires ${dayName}`, color: '#D4863A', urgent: false }
  }
  const dateStr = expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  if (daysUntil <= 14) {
    return { text: `Expires ${dateStr}`, color: '#7A7468', urgent: false }
  }
  return { text: `Best by ${dateStr}`, color: '#B0A99F', urgent: false }
}

// ─── calculateItemStatus ──────────────────────────────────────────────────────

// Define the five possible status values an item can have
export type ItemStatus = 'green' | 'amber' | 'red' | 'learning' | 'used'

// Define the shape of the object returned by calculateItemStatus
interface ItemStatusResult {
  // The traffic-light status of this pantry item
  status: ItemStatus
  // How depleted the item is, expressed as a fraction (0 = just restocked, 1 = fully depleted)
  depletion: number
  // How many days are estimated to remain before the item runs out
  daysRemaining: number
}

// Calculate the current status of a pantry item based on usage history
export function calculateItemStatus(
  // The last date the user confirmed this item was in stock (null if never confirmed)
  lastConfirmedAt: Date | null,
  // The average number of days between restocks, learned from history (null if unknown)
  avgRestockCycleDays: number | null,
  // How many times the user has confirmed this item so far
  confirmationCount: number,
  // Optional manual override set by the user
  statusOverride?: string | null,
  // Household's average shopping interval — used as a fallback for learning items
  householdShopInterval?: number | null,
): ItemStatusResult {
  // Apply manual override before running the pattern engine
  if (statusOverride === 'used') {
    return { status: 'used', depletion: 1, daysRemaining: 0 }
  }
  if (statusOverride === 'out') {
    return { status: 'red', depletion: 1, daysRemaining: -1 }
  }

  // Items still in learning — use household shopping interval as a fallback estimate
  if (confirmationCount < 3 || lastConfirmedAt === null) {
    if (householdShopInterval && lastConfirmedAt) {
      const daysSince  = (Date.now() - lastConfirmedAt.getTime()) / (1000 * 60 * 60 * 24)
      const depletion  = daysSince / householdShopInterval
      const daysRemaining = householdShopInterval - daysSince

      if (depletion >= 0.9) return { status: 'red',   depletion, daysRemaining }
      if (depletion >= 0.7) return { status: 'amber', depletion, daysRemaining }
    }
    return { status: 'learning', depletion: 0, daysRemaining: -1 }
  }

  // Without a cycle length we cannot estimate depletion
  if (avgRestockCycleDays === null) {
    // Return learning state — cycle not established yet
    return { status: 'learning', depletion: 0, daysRemaining: -1 }
  }

  // Capture the current timestamp so all calculations use a consistent "now"
  const now = new Date()

  // Calculate the number of milliseconds that have elapsed since the last confirmation
  const elapsedMs = now.getTime() - lastConfirmedAt.getTime()

  // Convert milliseconds to days (1 day = 86 400 000 ms)
  const daysSince = elapsedMs / 86_400_000

  // Depletion is the fraction of the cycle that has already been consumed
  const depletion = daysSince / avgRestockCycleDays

  // Days remaining is how many days are left before the full cycle completes
  const daysRemaining = avgRestockCycleDays - daysSince

  // At 90% or more of the cycle the item is considered critically low
  if (depletion >= 0.9) {
    // Return red status — item is nearly or fully depleted
    return { status: 'red', depletion, daysRemaining }
  }

  // At 70% or more of the cycle the item is getting low
  if (depletion >= 0.7) {
    // Return amber status — item will run out soon
    return { status: 'amber', depletion, daysRemaining }
  }

  // Below 70% the item is considered well-stocked
  return { status: 'green', depletion, daysRemaining }
}

// ─── updateRestockCycle ───────────────────────────────────────────────────────

// Define the shape of the object returned by updateRestockCycle
interface RestockCycleResult {
  // The average number of days between restocks, derived from recent history
  avgCycleDays: number
  // How confident we are in this estimate, from 0.0 (no data) to 1.0 (fully confident)
  confidenceScore: number
}

// Recalculate the average restock cycle from the full list of confirmation dates
export function updateRestockCycle(
  // Every date the user has confirmed this item was in stock
  confirmationDates: Date[],
): RestockCycleResult {
  // We need at least two dates to measure any gap between them
  if (confirmationDates.length < 2) {
    // Return a sensible default cycle of 7 days and zero confidence
    return { avgCycleDays: 7, confidenceScore: 0 }
  }

  // Sort a copy of the dates from earliest to latest so gaps are always positive
  const sorted = [...confirmationDates].sort((a, b) => a.getTime() - b.getTime())

  // Gaps under this threshold are treated as duplicate/test confirmations
  const MIN_GAP_HOURS = 24
  // Gaps over this threshold are treated as outliers (long breaks, vacations, etc.)
  const MAX_GAP_DAYS  = 90

  // Build an array of raw gaps in days between each consecutive pair of dates
  const gaps: number[] = []

  for (let i = 1; i < sorted.length; i++) {
    const gapMs   = sorted[i].getTime() - sorted[i - 1].getTime()
    const gapDays = gapMs / (1000 * 60 * 60 * 24)
    gaps.push(gapDays)
  }

  // Keep only gaps within the valid range — discard same-day taps and outlier breaks
  const validGaps = gaps.filter(gap => gap * 24 >= MIN_GAP_HOURS && gap <= MAX_GAP_DAYS)

  // No valid gaps — all confirmations were either too close together or too far apart
  if (validGaps.length === 0) {
    return { avgCycleDays: 7, confidenceScore: 0 }
  }

  // Only the most recent 8 valid gaps are used — older data is less representative
  const recentGaps = validGaps.slice(-8)

  // Average gap in days across the recent window
  const avgCycleDays = recentGaps.reduce((sum, g) => sum + g, 0) / recentGaps.length

  // Confidence grows with more data points, capped at 1.0 once we have 10+ confirmations
  const confidenceScore = Math.min(confirmationDates.length / 10, 1.0)

  // Sanity check — reject any value that slipped through and would corrupt the DB
  if (avgCycleDays < 1 || avgCycleDays > 365) {
    return { avgCycleDays: 7, confidenceScore: 0 }
  }

  // Return the computed cycle length and confidence
  return { avgCycleDays, confidenceScore }
}

// ─── Dev-only self-tests ──────────────────────────────────────────────────────

// Only run these checks in development — they are stripped from production builds
if (import.meta.env.DEV) {
  // Helper: subtract N days from now and return the resulting Date
  const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000)

  // Test 1 — confirmed 3 days ago on a 10-day cycle → should be green
  const test1 = calculateItemStatus(daysAgo(3), 10, 5)
  // Log the result so it can be verified in the browser console
  console.log('[patternEngine] 3 days ago / 10-day cycle (expect green):', test1)

  // Test 2 — confirmed 8 days ago on a 10-day cycle → should be amber (80% depleted)
  const test2 = calculateItemStatus(daysAgo(8), 10, 5)
  // Log the result so it can be verified in the browser console
  console.log('[patternEngine] 8 days ago / 10-day cycle (expect amber):', test2)

  // Test 3 — confirmed 11 days ago on a 10-day cycle → should be red (110% depleted)
  const test3 = calculateItemStatus(daysAgo(11), 10, 5)
  // Log the result so it can be verified in the browser console
  console.log('[patternEngine] 11 days ago / 10-day cycle (expect red):', test3)

  // Test 4 — only 2 confirmations regardless of dates → should be learning
  const test4 = calculateItemStatus(daysAgo(3), 10, 2)
  // Log the result so it can be verified in the browser console
  console.log('[patternEngine] 2 confirmations only (expect learning):', test4)
}
